import type {
  ComponentPropsWithoutRef,
  ReactNode,
  KeyboardEvent,
  FocusEvent,
  ChangeEvent,
  MouseEvent,
} from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { VisuallyHidden, unstable_useId as useId } from 'reakit';
import { useMachine } from '@xstate/react';
import invariant from 'tiny-invariant';
import { createPopper } from '@popperjs/core';
import { pxToRem } from '@mos-cat/ds-theme';
import type { SearchContext } from './internal/context';
import { searchContext } from './internal/context';
import { searchMachine } from './search.machine';
import { SearchField } from './SearchField';
import type { supportedLanguages } from './internal/text';
import { createLanguageGetter } from './internal/text';
import { Inline } from '../Inline/Inline';
import { Box } from '../Box/Box';
import { SearchButton } from './SearchButton';
import { GridPopover } from './Grid';
import { sameWidth } from './internal/sameWidthModifier';
import type { ActionableOption, Option } from './internal/types';

let keysToNavigatePopover = [
  'Enter',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Escape',
];

type Props = Omit<
  ComponentPropsWithoutRef<'input'>,
  'type' | 'aria-controls' | 'aria-activedescendant' | 'onSelect'
> & {
  /**
   * Язык виджета.
   * @default "ru"
   */
  lang?: keyof typeof supportedLanguages;
  /**
   * Лейбл для поля поиска
   * @default "Поиск"
   */
  label?: string;
  /**
   * Содержимое поповера с подсказками.
   * Рекомендуется пробрасывать только компоненты-опции и разделитель.
   */
  children?: ReactNode;
  /**
   * Указывает, включено ли сохранение истории поиска
   */
  historyEnabled?: boolean;
  /**
   * Вызывается на выбор текстовой опции, опции из истории или ссылки.
   * @param selectedOptionValue - `value` опции.
   * @param selectedOptionData - Дополнительная информация об опции. Например, тип и `href`, если ссылка.
   */
  onSelect(
    selectedOptionValue: string,
    selectedOptionData: Pick<
      // ActionableOption это просто внутренний тип для текстовой опции с кнопкой. Его не нужно показывать наружу.
      Exclude<Option, ActionableOption>,
      'type' | 'href'
    >
  ): void;
  /**
   * Вызывается на нажатие кнопки "Найти" опцию "Перейти к результатам поиска" или нажатие Enter/Return в поле ввода, когда ни одна опция не выбрана.
   * Если не передано, запустит событие `"submit"` на форме к которой относится поле.
   */
  onSubmit?(): void;
  /**
   * Вызывается на нажатие кнопки удаления из истории в опции
   * @param deletedOptionValue - `value` опции
   */
  onDeleteFromHistory?(deletedOptionValue: string): void;
  /**
   * Вызывается на выбор опции "Сохранять/Не сохранять историю поиска".
   */
  onToggleHistory?(): void;
};

/**
 * Виджет поиска с поисковой строкой, кнопкой `submit`
 */
export const SearchWidget = forwardRef<HTMLInputElement, Props>(
  function SearchWidget(
    {
      label,
      lang,
      id,
      children,
      historyEnabled,
      onSelect,
      onSubmit,
      onDeleteFromHistory,
      onToggleHistory,
      ...props
    },
    ref
  ) {
    const fieldRef = useRef<HTMLInputElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Почему-то составители типов не подумали, что кто-то захочет прокинуть реф наружу и работать с ним внутри
    useImperativeHandle(ref, () => fieldRef.current!);
    const [state, send] = useMachine(searchMachine, {
      actions: {
        select_option(ctx, message) {
          const row =
            ctx.options.find((option) => option.id === message.id) ??
            ctx.activeRow;
          if (!row) return;
          const { value, type, href } = row;
          // "actionable" на текущий момент у нас является исключительно опцией из изстории, но фактически это текстовая опция с кнопкой
          // Если будет возможность использовать `actionable` тип в отрыве от опции из истории, тип саджеста всё равно будет "text"
          const parameters: Parameters<typeof onSelect>[1] = {
            type: type === 'actionable' ? 'text' : type,
          };
          if (type === 'link') {
            parameters.href = href;
          }
          onSelect(value, parameters);
        },
        delete(ctx, message) {
          const row =
            ctx.options.find((option) => option.id === message.id) ??
            ctx.activeRow;
          if (!row || !onDeleteFromHistory) return;
          onDeleteFromHistory(row.value);
        },
        submit() {
          buttonRef.current?.click();
        },
        toggle_history() {
          if (!onToggleHistory) return;
          onToggleHistory();
        },
      },
    });
    const getText = useMemo(() => createLanguageGetter(lang), [lang]);
    const { id: fieldId } = useId({ id });
    const { id: popoverId } = useId();
    invariant(fieldId && popoverId);
    const contextValue = useMemo<SearchContext>(
      () => ({
        lang: lang ?? 'ru',
        send,
        activeRow: state.context.activeRow,
        activeDescendant: state.context.activeDescendant,
        historyEnabled,
      }),
      [
        lang,
        send,
        state.context.activeDescendant,
        state.context.activeRow,
        historyEnabled,
      ]
    );

    useEffect(
      function scrollSelectedOptionIntoView() {
        if (state.context.activeRow === undefined) return;
        document
          .getElementById(state.context.activeRow.id)
          ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      },
      [state.context.activeRow]
    );

    const popoverRef = useRef<HTMLDivElement>(null);
    useEffect(
      function positionPopover(): undefined | (() => void) {
        if (!fieldRef.current) return;
        if (!popoverRef.current) return;
        if (!state.matches('open')) return;
        const popper = createPopper(fieldRef.current, popoverRef.current, {
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 12] },
            },
            // @ts-expect-error -- Очень сложно корректно типизировать.
            sameWidth,
          ],
        });
        // eslint-disable-next-line consistent-return -- Нам нужно уничтожить поппер на закрытие или анмаунт
        return () => popper.destroy();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Очередная булева переменная усложнит чтение файла
      [state.matches('open')]
    );

    return (
      <searchContext.Provider value={contextValue}>
        <Inline space="sp-8">
          <Box
            as="label"
            flexGrow={1}
            flexShrink={1}
            // Минимальная ширина поля внутри острова при которой для разрешения 320 не будет переноса
            flexBasis={pxToRem(144)}
            role="combobox"
            aria-haspopup="grid"
            aria-controls={popoverId}
            aria-expanded={state.matches('open')}
          >
            <VisuallyHidden>{label ?? getText('label')}</VisuallyHidden>
            <SearchField
              ref={fieldRef}
              {...props}
              type="search"
              aria-controls={popoverId}
              aria-activedescendant={state.context.activeDescendant}
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              onFocus={(event: FocusEvent<HTMLInputElement>) => {
                props.onFocus?.(event);
                if (event.defaultPrevented) return;
                send('focus');
              }}
              onBlur={(event: FocusEvent<HTMLInputElement>) => {
                props.onBlur?.(event);
                if (event.defaultPrevented) return;
                send('blur');
              }}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                props.onKeyDown?.(event);
                if (event.defaultPrevented) return;
                if (!keysToNavigatePopover.includes(event.key)) return;
                if (state.matches('closed') && event.key === 'Enter') {
                  event.preventDefault();
                }
                // @ts-expect-error -- Типизировать MouseEvent и KeyboardEvent можно, но очень сложно и требует влезания в стандартную библиотеку типов.
                send(event);
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                props.onChange?.(event);
                if (event.defaultPrevented) return;
                send('change');
              }}
              onClick={(event: MouseEvent<HTMLInputElement>) => {
                props.onClick?.(event);
                if (event.defaultPrevented) return;
                send('click');
              }}
            />
          </Box>
          <SearchButton
            ref={buttonRef}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              if (onSubmit) {
                event.preventDefault();
                onSubmit();
              }
            }}
          />
        </Inline>
        <GridPopover
          ref={popoverRef}
          id={popoverId}
          display={state.matches('open') ? 'block' : 'none'}
        >
          {children}
        </GridPopover>
      </searchContext.Provider>
    );
  }
);
